'use client'

import { useTheme } from 'next-themes'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkflowStep } from '@/types/synoptique'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import SmartLink from '@/components/smart-link'
import { AppLogo } from '@/components/app-logo'
import { AuthButton } from '@/components/auth/auth-button'
import { SaveIndicator } from '@/components/synoptic/save-indicator'
import { ShareDialog } from './ShareDialog'
import {
  ChevronRight,
  ChevronDown,
  Download,
  Eye,
  Grid3X3,
  Monitor,
  Zap,
  Upload,
  Save,
  Cloud,
  FileUp,
  FileDown,
  Share2,
  Sun,
  Moon,
} from 'lucide-react'

interface SynoptiqueHeaderProps {
  readOnly: boolean
  projectName: string
  setProjectName: (name: string) => void
  currentProject: { id: string; name: string } | null
  isPro: boolean
  rawIsPro: boolean
  tempProjectName: string | null
  setTempProjectName: (name: string | null) => void
  updateProjectName: ((name: string) => void) | undefined
  currentStep: WorkflowStep
  handleStepChange: (step: WorkflowStep) => void
  showShareDialog: boolean
  setShowShareDialog: (v: boolean) => void
  saveProject: ((data: Record<string, unknown>, opts?: { debounce?: boolean }) => void) | undefined
  dataPages: unknown[]
  powerPages: unknown[]
  collectSynopticData: () => Record<string, unknown>
  isSavingDebug: boolean
  lastSaveTime: number
  saveStartTime: number | null
  timeSinceLastSave: number
  isOnline: boolean
  handleSaveSynoptic: () => void
  handleSaveAsSynoptic: () => void
  handleImportSynopticFile: () => void
  handleExportSynopticFile: () => void
  setShowProjectPicker: (v: boolean) => void
  supabaseConfigured: boolean
  demoMode: { state: { isDemo: boolean } }
  getSaveButtonColors: (timeElapsed: number) => { border: string; bg: string }
}

export function SynoptiqueHeader({
  readOnly,
  projectName,
  setProjectName,
  currentProject,
  isPro,
  rawIsPro,
  tempProjectName,
  setTempProjectName,
  updateProjectName,
  currentStep,
  handleStepChange,
  showShareDialog,
  setShowShareDialog,
  saveProject,
  dataPages,
  powerPages,
  collectSynopticData,
  isSavingDebug,
  lastSaveTime,
  saveStartTime,
  timeSinceLastSave,
  isOnline,
  handleSaveSynoptic,
  handleSaveAsSynoptic,
  handleImportSynopticFile,
  handleExportSynopticFile,
  setShowProjectPicker,
  supabaseConfigured,
  demoMode,
  getSaveButtonColors,
}: SynoptiqueHeaderProps) {
  const { t } = useLanguage()
  const { theme, setTheme } = useTheme()

  return (
    <header className="relative z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full px-6">
        <div className="relative flex h-16 items-center">
          {/* Logo + Titre + Nom du projet à gauche */}
          <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
            <SmartLink href="/grid" className="cursor-pointer hover:opacity-80 transition-opacity">
              <AppLogo />
            </SmartLink>
            <div>
              <h1 className="text-xl font-bold">VisualGrid</h1>
              <p className="text-xs text-muted-foreground">Interface Synoptique LED</p>
            </div>
            <div className="ml-4 flex items-center gap-2">
              {readOnly ? (
                <span className="text-sm font-medium truncate max-w-[200px]">{projectName}</span>
              ) : (
              <Input
                type="text"
                value={currentProject && isPro ? (tempProjectName !== null ? tempProjectName : currentProject.name) : projectName}
                onChange={(e) => {
                  if (currentProject && isPro) {
                    // Pour les utilisateurs Pro avec projet, stocker temporairement (autoriser vide)
                    setTempProjectName(e.target.value);
                  } else {
                    // Pour les autres, utiliser l'état local
                    setProjectName(e.target.value);
                  }
                }}
                onBlur={() => {
                  // Sauvegarder quand l'utilisateur clique à côté
                  if (currentProject && isPro && updateProjectName && tempProjectName !== null) {
                    const newName = tempProjectName.trim() || "Sans titre";
                    if (newName !== currentProject.name) {
                      updateProjectName(newName);
                    }
                    setTempProjectName(null); // Réinitialiser l'état temporaire
                  }
                }}
                onKeyDown={(e) => {
                  // Sauvegarder quand l'utilisateur appuie sur Entrée
                  if (e.key === 'Enter' && currentProject && isPro && updateProjectName && tempProjectName !== null) {
                    const newName = tempProjectName.trim() || "Sans titre";
                    if (newName !== currentProject.name) {
                      updateProjectName(newName);
                    }
                    setTempProjectName(null); // Réinitialiser l'état temporaire
                    e.currentTarget.blur(); // Enlever le focus
                  }
                }}
                placeholder={t("placeholder.projectName")}
                className="h-8 text-sm w-56 px-3 border-transparent bg-transparent hover:border-input hover:bg-background focus:border-input focus:bg-background transition-colors"
              />
              )}
              {readOnly && (
                <Badge variant="secondary" className="text-xs gap-1 shrink-0">
                  <Eye className="h-3 w-3" />
                  {t('synoptic.share.readOnlyBanner')}
                </Badge>
              )}
            </div>
          </div>

          {/* Workflow au centre — positionné en absolu pour un centrage parfait */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-4 z-10" data-onboarding="workflow-tabs">
            {/* Étapes du Workflow - Version complète */}
            <div className="hidden lg:flex items-center space-x-1">
              {[
                { step: "testPattern" as WorkflowStep, label: "Mire", number: 1, icon: Grid3X3, disabled: readOnly },
                { step: "data" as WorkflowStep, label: "Data", number: 2, icon: Monitor, disabled: false },
                { step: "cabling" as WorkflowStep, label: "Power", number: 3, icon: Zap, disabled: false },
                { step: "export" as WorkflowStep, label: "Export", number: 4, icon: Download, disabled: false }
              ].map((item, index) => (
                <div key={item.step} className="flex items-center">
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => !item.disabled && handleStepChange(item.step)}
                      data-onboarding={`step-${item.step}`}
                      disabled={item.disabled}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${item.disabled
                        ? 'opacity-40 cursor-not-allowed'
                        : currentStep === item.step
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border ${item.disabled
                        ? 'border-muted-foreground/40 text-muted-foreground/40'
                        : currentStep === item.step
                          ? 'bg-primary-foreground text-primary border-primary-foreground'
                          : 'border-muted-foreground text-muted-foreground'
                        }`}>
                        {item.number}
                      </div>
                      <item.icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Button>
                    {item.disabled && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-popover border rounded-md shadow-lg text-xs text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999]">
                        {t('synoptic.share.mireDisabled')}
                      </div>
                    )}
                  </div>
                  {index < 3 && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />
                  )}
                </div>
              ))}
            </div>

            {/* Étapes du Workflow - Version compacte */}
            <div className="flex lg:hidden items-center space-x-1">
              {[
                { step: "testPattern" as WorkflowStep, number: 1, icon: Grid3X3, disabled: readOnly },
                { step: "data" as WorkflowStep, number: 2, icon: Monitor, disabled: false },
                { step: "cabling" as WorkflowStep, number: 3, icon: Zap, disabled: false },
                { step: "export" as WorkflowStep, number: 4, icon: Download, disabled: false }
              ].map((item, index) => (
                <div key={item.step} className="flex items-center">
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => !item.disabled && handleStepChange(item.step)}
                      data-onboarding={`step-${item.step}`}
                      disabled={item.disabled}
                      className={`flex items-center space-x-1 px-2 py-2 rounded-lg transition-all ${item.disabled
                        ? 'opacity-40 cursor-not-allowed'
                        : currentStep === item.step
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                    >
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold border ${item.disabled
                        ? 'border-muted-foreground/40 text-muted-foreground/40'
                        : currentStep === item.step
                          ? 'bg-primary-foreground text-primary border-primary-foreground'
                          : 'border-muted-foreground text-muted-foreground'
                        }`}>
                        {item.number}
                      </div>
                      <item.icon className="h-3 w-3" />
                    </Button>
                    {item.disabled && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-popover border rounded-md shadow-lg text-xs text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999]">
                        {t('synoptic.share.mireDisabled')}
                      </div>
                    )}
                  </div>
                  {index < 3 && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions à droite */}
          <div className="flex items-center space-x-3 flex-shrink-0 ml-auto z-20">
            {/* Bouton de partage (uniquement pour le propriétaire avec un projet sauvegardé) */}
            {!readOnly && currentProject && rawIsPro && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 flex-shrink-0"
                onClick={() => setShowShareDialog(true)}
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">{t('synoptic.share')}</span>
              </Button>
            )}

            {/* ShareDialog */}
            <ShareDialog
              open={showShareDialog}
              onOpenChange={setShowShareDialog}
              projectId={currentProject?.id || null}
              projectName={currentProject?.name || projectName}
            />

            {/* Indicateur de sauvegarde pour les utilisateurs Pro */}
            {!readOnly && isPro && (
              <SaveIndicator
                onSave={() => {
                  // Forcer une sauvegarde immédiate avec les données actuelles
                  if (saveProject && dataPages && powerPages) {
                    // Utiliser collectSynopticData() pour inclure TOUTES les données (y compris backupProcessors)
                    const synopticData = collectSynopticData()
                    saveProject(synopticData, { debounce: false })
                  }
                }}
              />
            )}

            {/* Menu Import/Export de fichiers .vgsyn (masqué en mode readOnly) */}
            {!readOnly && <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 flex-shrink-0" data-onboarding="save-menu">
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('action.importExport') || 'Import/Export'}</span>
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {/* Section sauvegarde - PRIORITAIRE */}
                <DropdownMenuItem
                  onClick={handleSaveSynoptic}
                  className={`cursor-pointer transition-all duration-500 ease-in-out relative z-50 ${!isSavingDebug && lastSaveTime
                    ? `${getSaveButtonColors(timeSinceLastSave).border} ${getSaveButtonColors(timeSinceLastSave).bg}`
                    : ''
                    } hover:scale-[1.02] active:scale-[0.98]`}
                  disabled={readOnly || isSavingDebug || !isPro || demoMode.state.isDemo}
                >
                  {isSavingDebug ? (
                    <div className="mr-3 h-4 w-4 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
                  ) : (
                    <Save className={`mr-3 h-4 w-4 ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : 'text-green-500'}`} />
                  )}
                  <div className="flex flex-col">
                    <span className={`font-medium ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : ''}`}>
                      {isSavingDebug
                        ? (t('synoptic.savingInProgress') || 'Sauvegarde en cours...')
                        : (t('synoptic.save') || 'Sauvegarder (Ctrl+S)')
                      }
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {!isPro || demoMode.state.isDemo
                        ? (t('synoptic.proOnlyFeature') || 'Fonctionnalité Pro uniquement')
                        : isSavingDebug
                          ? (t('synoptic.saveDuration') || `Durée: ${Math.round((Date.now() - (saveStartTime || 0)) / 1000)}s`).replace('{seconds}', String(Math.round((Date.now() - (saveStartTime || 0)) / 1000)))
                          : (t('synoptic.saveCloudDesc') || 'Sauvegardez votre projet localement et dans le cloud')
                      }
                    </span>
                    {!isOnline && isPro && !demoMode.state.isDemo && (
                      <span className="text-xs text-orange-500">🌐 {t('synoptic.offlineWarning') || 'Hors connexion'}</span>
                    )}
                  </div>
                </DropdownMenuItem>


                <DropdownMenuItem
                  onClick={handleSaveAsSynoptic}
                  className="cursor-pointer"
                  disabled={!isPro || demoMode.state.isDemo}
                >
                  <Save className={`mr-3 h-4 w-4 ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : 'text-blue-500'}`} />
                  <div className="flex flex-col">
                    <span className={`font-medium ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : ''}`}>{t('synoptic.saveAs') || 'Sauvegarder sous...'}</span>
                    <span className="text-xs text-muted-foreground">{!isPro || demoMode.state.isDemo ? (t('synoptic.proOnlyFeature') || 'Fonctionnalité Pro uniquement') : (t('synoptic.saveAsDesc') || 'Sauvegarder sous un nouveau nom')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* Section Cloud (Pro uniquement) */}
                {isPro && supabaseConfigured && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setShowProjectPicker(true)}
                      className="cursor-pointer"
                    >
                      <Cloud className="mr-3 h-4 w-4 text-blue-500" />
                      <div className="flex flex-col">
                        <span className="font-medium">{t('synoptic.loadFromCloud') || 'Charger depuis le Cloud'}</span>
                        <span className="text-xs text-muted-foreground">{t('synoptic.loadFromCloudDesc') || 'Ouvrir un projet cloud existant'}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Section fichiers locaux */}
                <DropdownMenuItem
                  onClick={handleImportSynopticFile}
                  className="cursor-pointer"
                  disabled={!isPro || demoMode.state.isDemo}
                >
                  <FileUp className={`mr-3 h-4 w-4 ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : 'text-purple-500'}`} />
                  <div className="flex flex-col">
                    <span className={`font-medium ${!isPro || demoMode.state.isDemo ? 'text-muted-foreground' : ''}`}>{t('synoptic.importFromFile') || 'Importer un fichier .vgsyn'}</span>
                    <span className="text-xs text-muted-foreground">{!isPro || demoMode.state.isDemo ? (t('synoptic.proOnlyFeature') || 'Fonctionnalité Pro uniquement') : (t('synoptic.importFromFileDesc') || 'Charger depuis un fichier local')}</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExportSynopticFile}
                  className="cursor-pointer"
                >
                  <FileDown className="mr-3 h-4 w-4 text-indigo-500" />
                  <div className="flex flex-col">
                    <span className="font-medium">{t('synoptic.exportToFile') || 'Exporter en .vgsyn'}</span>
                    <span className="text-xs text-muted-foreground">{t('synoptic.exportToFileDesc') || 'Télécharger le projet actuel'}</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>}

            {/* Bouton toggle thème sombre/clair */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? t('title.switchToLight') : t('title.switchToDark')}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* Bouton Mon Compte (photo de profil uniquement) */}
            <AuthButton variant="outline" size="sm" showText={false} avatarOnly />
          </div>
        </div>
      </div>
    </header>
  )
}
